<template>
  <div class="w-full">
    <div
      v-if="items"
      class="w-full hidden sm:flex justify-between items-center flex-wrap gap-[10px]"
    >
      <div class="w-fit flex items-center gap-[10px]">
        <div v-if="items.from" class="PaginationLabel">
          {{ `แสดงผล ${items.from} - ${items.to} จาก ${items.total}` }}
        </div>
        <div v-else class="PaginationLabel">
          {{ `แสดงผล 0 - 0 จาก 0` }}
        </div>
        <!-- set perpage -->
        <SelectBoxComponent
          :selectContainerClass="'!min-w-[156px]'"
          :inputId="perPageId + '1'"
          :keyValue="'id'"
          :keyName="'name'"
          :options="numberDataOption"
          :value="perPageNum"
          :placeholder="'แสดง 10 รายการ'"
          :selfSelected="true"
          @update:modelValue="setNumberData"
        />
      </div>
      <!-- set perpage -->
      <div class="flex">
        <ul class="pagination">
          <!-- ปุ่มก่อนหน้า -->
          <li class="page-item" :class="{ disabled: items.current_page === 1 }">
            <a
              href="#"
              @click.prevent="getData(items.current_page - 1)"
              class="page-link"
            >
              <CaretDownIcon
                class="text-[#8D8D8D] w-[18px] h-[18px] rotate-90"
              />
            </a>
          </li>

          <!-- ปุ่มเลขหน้า -->
          <template v-for="(page, index) in paginationPages" :key="index">
            <li v-if="page === '...'" class="page-item cursor-not-allowed">
              <span class="page-link PaginationLabel">...</span>
            </li>
            <li
              v-else
              :class="['page-item', { active: items.current_page === page }]"
              @click="getData(page)"
            >
              <span class="page-link pagi_link PaginationLabel">
                {{ page }}
              </span>
            </li>
          </template>

          <!-- ปุ่มถัดไป -->
          <li
            class="page-item"
            :class="{ disabled: items.current_page === items.last_page }"
          >
            <a
              href="#"
              @click.prevent="getData(items.current_page + 1)"
              class="page-link"
            >
              <CaretDownIcon
                class="text-[#8D8D8D] w-[18px] h-[18px] rotate-270"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Mobile pagination -->
    <div v-if="items" class="w-full flex flex-col gap-[10px] sm:hidden">
      <!-- set perpage -->
      <SelectBoxComponent
        :selectContainerClass="'!min-w-[156px]'"
        :inputId="perPageId + '2'"
        :keyValue="'id'"
        :keyName="'name'"
        :options="numberDataOption"
        :value="perPageNum"
        :placeholder="'แสดง 10 รายการ'"
        :selfSelected="true"
        @update:modelValue="setNumberData"
      />
      <div class="w-full flex justify-between items-start">
        <!-- set perpage -->
        <ButtonComponent
          :label="'ก่อนหน้า'"
          :bClass="[
            'btn_shadow--preAndNext',
            { disabled: items.current_page === 1 },
          ]"
          @clicked="getData(items.current_page - 1)"
        />
        <ButtonComponent
          :label="'ต่อไป'"
          :bClass="[
            'btn_shadow--preAndNext',
            { disabled: items.current_page === items.last_page },
          ]"
          @clicked="getData(items.current_page + 1)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  items: {
    type: Object,
    required: false,
  },
  perPageNum: {
    type: Number,
    default: 10,
  },
  perPageId: {
    type: String,
    default: "perpage",
  },
});

const emit = defineEmits(["selectedPage"]);

const getData = (page) => {
  if (page >= 1 && page <= (props.items?.last_page || 1)) {
    emit("selectedPage", page, props.items?.per_page);
  }
};

const ALL_VALUE = "ALL";

const numberDataOption = computed(() => [
  { id: 10, name: "แสดง 10 รายการ" },
  { id: 20, name: "แสดง 20 รายการ" },
  { id: 30, name: "แสดง 30 รายการ" },
  { id: ALL_VALUE, name: "แสดงทั้งหมด" },
]);

const setNumberData = (value) => {
  const total = Number(props.items?.total ?? 0);

  const perPage = value === ALL_VALUE ? total : value;

  emit("selectedPage", 1, perPage);
};

const paginationPages = computed(() => {
  const total = props.items?.last_page || 1;
  const current = props.items?.current_page || 1;
  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);

    if (current > 4) pages.push("...");

    for (let i = current - 2; i <= current + 2; i++) {
      if (i > 1 && i < total) pages.push(i);
    }

    if (current < total - 3) pages.push("...");

    pages.push(total);
  }

  return pages;
});
</script>

<style lang="scss" scoped>
.PaginationLabel {
  color: #1f1d1f;
  text-align: center;
  font-family: Kanit;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.1px;

  white-space: nowrap;
}

.pagination {
  display: flex;
  flex-direction: row;
  border-radius: 4px;
  width: fit-content;
  background: white;

  li {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dfdcdd !important;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;

    &:first-child {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }

    &:last-child {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  .page-item {
    &.active {
      pointer-events: none;
      user-select: none;
      background-color: #9b237b;
      border: 1px solid #9b237b !important;

      span {
        color: #ffffff !important;
      }
    }

    &.disabled {
      pointer-events: none;
      user-select: none;
      color: #b7b2b5;
    }
  }
}
</style>
